import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Badge,
  LinearProgress,
  Alert,
  Tooltip,
  Fade,
  Collapse
} from '@mui/material';
import {
  CheckCircle as KeepIcon,
  VolunteerActivism as DonateIcon,
  HelpOutline as NotSureIcon,
  AutoAwesome as AIIcon,
  ArrowBack as BackIcon,
  Inventory as ExitBinIcon,
  Refresh as RefreshIcon,
  ViewList as ListIcon,
  ViewCarousel as SwipeIcon,
  AcUnit as WinterIcon,
  WbSunny as SummerIcon,
  Spa as SpringIcon,
  Park as FallIcon,
  Schedule as TimeIcon,
  ContentCopy as DuplicateIcon
} from '@mui/icons-material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import Layout from '../components/Layout';
import letGoService from '../services/letGoService';
import { getBackendBaseUrl } from '../utils/configUtils';
import { colors } from '../styles/styleConstants';

const LetGo = () => {
  const navigate = useAppNavigation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scanTriggered = useRef(false);
  const [suggestions, setSuggestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [stats, setStats] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [viewMode, setViewMode] = useState('swipe'); // 'swipe', 'list', or 'duplicates'
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [analyzingGroup, setAnalyzingGroup] = useState(null);
  const [aiScanLoading, setAiScanLoading] = useState(false);
  const [aiScanResult, setAiScanResult] = useState(null);

  const currentItem = suggestions[currentIndex];
  const progress = suggestions.length > 0 ? ((currentIndex) / suggestions.length) * 100 : 0;
  const isComplete = currentIndex >= suggestions.length && suggestions.length > 0;

  const fetchSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await letGoService.getSuggestions(20);
      setSuggestions(data.suggestions || []);
      setCurrentIndex(0);
      setAiRecommendation(null);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Failed to load suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await letGoService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchDuplicates = useCallback(async () => {
    try {
      setDuplicatesLoading(true);
      console.log('Fetching duplicates...');
      const data = await letGoService.getDuplicates(10);
      console.log('Duplicates response:', data);
      setDuplicates(data.duplicate_groups || []);
    } catch (err) {
      console.error('Error fetching duplicates:', err);
    } finally {
      setDuplicatesLoading(false);
    }
  }, []);

  const runAIScan = useCallback(async () => {
    try {
      setAiScanLoading(true);
      setAiScanResult(null);
      setLoading(true);
      const result = await letGoService.runAIScan(20);
      setAiScanResult(result);
      if (result.success && result.suggestions?.length > 0) {
        setSuggestions(result.suggestions);
        setCurrentIndex(0);
        setViewMode('list');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error running AI scan:', err);
      setError('Failed to run AI scan. Please try again.');
      setLoading(false);
    } finally {
      setAiScanLoading(false);
    }
  }, []);

  // Check if we should trigger AI scan (from Home page Declutter button)
  useEffect(() => {
    const shouldScan = searchParams.get('scan') === 'true';
    
    if (shouldScan && !scanTriggered.current) {
      scanTriggered.current = true;
      setSearchParams({});
      runAIScan();
    } else if (!shouldScan && !scanTriggered.current) {
      fetchSuggestions();
    }
    fetchStats();
    fetchDuplicates();
  }, [searchParams, setSearchParams, runAIScan, fetchSuggestions, fetchStats, fetchDuplicates]);

  const handleAIScan = async () => {
    await runAIScan();
  };

  const handleDecision = async (decision) => {
    if (!currentItem || decisionLoading) return;

    try {
      setDecisionLoading(true);
      await letGoService.recordDecision(currentItem.id, decision);
      
      // Move to next item
      setCurrentIndex(prev => prev + 1);
      setAiRecommendation(null);
      
      // Refresh stats
      fetchStats();
    } catch (err) {
      console.error('Error recording decision:', err);
      setError('Failed to record decision. Please try again.');
    } finally {
      setDecisionLoading(false);
    }
  };

  // Handle decision from list view - removes item from list
  const handleListDecision = async (itemId, decision, index) => {
    if (decisionLoading) return;

    try {
      setDecisionLoading(true);
      await letGoService.recordDecision(itemId, decision);
      
      // Remove item from suggestions list
      setSuggestions(prev => prev.filter((_, i) => i !== index));
      
      // Refresh stats
      fetchStats();
    } catch (err) {
      console.error('Error recording decision:', err);
      setError('Failed to record decision. Please try again.');
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleAskAI = async () => {
    if (!currentItem || aiLoading) return;

    try {
      setAiLoading(true);
      setAiRecommendation(null);
      const data = await letGoService.getAIRecommendation(currentItem.id);
      setAiRecommendation(data);
    } catch (err) {
      console.error('Error getting AI recommendation:', err);
      setAiRecommendation({
        success: false,
        error: 'Failed to get AI recommendation'
      });
    } finally {
      setAiLoading(false);
    }
  };

  const getImageUrl = (item) => {
    if (!item?.image_url) return '/placeholder.png';
    // Always use backend proxy for consistent image loading
    return `${getBackendBaseUrl()}/api/v2/files/item/${item.id}/${item.user_id || 0}`;
  };

  if (loading) {
    return (
      <Layout title="Let Go">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  // Toolbar action buttons for the header
  const headerActions = (
    <Box sx={{ display: 'flex', gap: 0.5, overflow: 'visible', flexShrink: 0 }}>
      <Tooltip title="Swipe View">
        <IconButton 
          onClick={() => setViewMode('swipe')}
          color={viewMode === 'swipe' ? 'primary' : 'default'}
          size="small"
        >
          <SwipeIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="List View">
        <IconButton 
          onClick={() => setViewMode('list')}
          color={viewMode === 'list' ? 'primary' : 'default'}
          size="small"
        >
          <ListIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={`Duplicates${duplicates.length > 0 ? ` (${duplicates.length})` : ''}`}>
        <IconButton 
          onClick={() => setViewMode('duplicates')}
          color={viewMode === 'duplicates' ? 'warning' : 'default'}
          size="small"
        >
          <DuplicateIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="View Exit Bin">
        <IconButton onClick={() => navigate.to.EXIT_BIN()} color="primary" size="small">
          <Badge 
            badgeContent={stats?.exit_bin_count || 0} 
            color="error"
            max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
          >
            <ExitBinIcon />
          </Badge>
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Layout title="Let Go" action={headerActions} disableBackButton={false}>
      <Box sx={{ maxWidth: { xs: '100%', md: 900 }, mx: 'auto', px: { xs: 1, sm: 2, md: 3 }, pb: 4 }}>
        {/* Progress */}
        {suggestions.length > 0 && !isComplete && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {currentIndex + 1} of {suggestions.length} items
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}% reviewed
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* AI Scan Button */}
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          size="large"
          startIcon={aiScanLoading ? <CircularProgress size={20} color="inherit" /> : <AIIcon />}
          onClick={handleAIScan}
          disabled={aiScanLoading}
          sx={{ mb: 3 }}
        >
          {aiScanLoading ? 'Scanning your items...' : '🔍 Find Items to Let Go (AI Scan)'}
        </Button>

        {/* AI Scan Result Message */}
        {aiScanResult && (
          <Alert 
            severity={aiScanResult.success ? 'success' : 'warning'} 
            sx={{ mb: 2 }}
            onClose={() => setAiScanResult(null)}
          >
            {aiScanResult.message || (aiScanResult.success 
              ? `AI found ${aiScanResult.suggestions?.length || 0} items to consider letting go!`
              : 'No items found to suggest.'
            )}
          </Alert>
        )}

        {/* Completion State */}
        {isComplete && (
          <Fade in>
            <Card sx={{ textAlign: 'center', p: 4 }}>
              <KeepIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                All Done!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                You've reviewed all suggested items.
              </Typography>
              {stats && (
                <Box sx={{ mb: 3 }}>
                  <Chip label={`${stats.exit_bin_count} in Exit Bin`} sx={{ m: 0.5 }} />
                  <Chip label={`${stats.keep_count} kept`} sx={{ m: 0.5 }} />
                  <Chip label={`${stats.donated_count} donated`} sx={{ m: 0.5 }} />
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchSuggestions}
                >
                  Check Again
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ExitBinIcon />}
                  onClick={() => navigate.to.EXIT_BIN()}
                >
                  View Exit Bin
                </Button>
              </Box>
            </Card>
          </Fade>
        )}

        {/* Empty State */}
        {!loading && suggestions.length === 0 && !isComplete && (
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" gutterBottom>
              No Items to Review
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Great job! All your items have been searched recently or already reviewed.
            </Typography>
            <Button variant="outlined" onClick={() => navigate.to.HOME()}>
              Back to Home
            </Button>
          </Card>
        )}

        {/* LIST VIEW */}
        {viewMode === 'list' && suggestions.length > 0 && !isComplete && (
          <Box sx={{ mb: 3 }}>
            {suggestions.map((item, index) => (
              <Card key={item.id} sx={{ mb: 2, display: 'flex', alignItems: 'stretch', overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  sx={{ width: { xs: 80, sm: 100, md: 120 }, height: { xs: 80, sm: 100, md: 120 }, objectFit: 'cover', flexShrink: 0 }}
                  image={getImageUrl(item)}
                  alt={item.name}
                  onError={(e) => { e.target.src = '/placeholder.png'; }}
                />
                <CardContent sx={{ flex: 1, py: { xs: 1, md: 2 }, px: { xs: 1, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="subtitle1" fontWeight="medium" noWrap sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                    {item.suggestion_reason || "Not searched recently"}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, mt: { xs: 1, md: 1.5 }, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleListDecision(item.id, 'keep', index)}
                      disabled={decisionLoading}
                      sx={{ minWidth: { xs: 50, sm: 60, md: 80 }, py: { xs: 0.25, md: 0.75 }, px: { xs: 1, sm: 1.5, md: 2 }, fontSize: { xs: '0.7rem', sm: '0.8125rem', md: '0.9rem' } }}
                    >
                      Keep
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={() => handleListDecision(item.id, 'donate', index)}
                      disabled={decisionLoading}
                      sx={{ minWidth: { xs: 50, sm: 60, md: 80 }, py: { xs: 0.25, md: 0.75 }, px: { xs: 1, sm: 1.5, md: 2 }, fontSize: { xs: '0.7rem', sm: '0.8125rem', md: '0.9rem' } }}
                    >
                      Donate
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleListDecision(item.id, 'not_sure', index)}
                      disabled={decisionLoading}
                      sx={{ minWidth: { xs: 40, sm: 50, md: 70 }, py: { xs: 0.25, md: 0.75 }, px: { xs: 1, sm: 1.5, md: 2 }, fontSize: { xs: '0.7rem', sm: '0.8125rem', md: '0.9rem' } }}
                    >
                      Skip
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* DUPLICATES VIEW */}
        {viewMode === 'duplicates' && (
          <Box sx={{ mb: 3 }}>
            {duplicatesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : duplicates.length === 0 ? (
              <Card sx={{ textAlign: 'center', p: 4 }}>
                <KeepIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Duplicates Found
                </Typography>
                <Typography color="text.secondary">
                  Great job! You don't have any similar items that might be duplicates.
                </Typography>
              </Card>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  We found {duplicates.length} groups of similar items. Consider keeping just the best one!
                </Alert>
                {duplicates.map((group, groupIndex) => (
                  <Card key={groupIndex} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {group.suggestion_reason}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {group.items.map((item) => (
                          <Card key={item.id} sx={{ width: 120, flexShrink: 0 }}>
                            <CardMedia
                              component="img"
                              height="80"
                              image={getImageUrl(item)}
                              alt={item.name}
                              sx={{ objectFit: 'cover', bgcolor: 'grey.100' }}
                              onError={(e) => { e.target.src = '/placeholder.png'; }}
                            />
                            <CardContent sx={{ p: 1 }}>
                              <Typography variant="caption" noWrap>
                                {item.name}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={analyzingGroup === groupIndex ? <CircularProgress size={16} /> : <AIIcon />}
                          onClick={async () => {
                            setAnalyzingGroup(groupIndex);
                            try {
                              const result = await letGoService.analyzeDuplicates(
                                group.items.map(i => i.id)
                              );
                              if (result.success) {
                                alert(`AI recommends keeping item ID ${result.recommendation}: ${result.reasoning}`);
                              } else {
                                alert(result.error || 'Could not analyze items');
                              }
                            } catch (err) {
                              console.error('Error analyzing duplicates:', err);
                            } finally {
                              setAnalyzingGroup(null);
                            }
                          }}
                          disabled={analyzingGroup !== null}
                        >
                          {analyzingGroup === groupIndex ? 'Analyzing...' : 'Ask AI Which to Keep'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </Box>
        )}

        {/* SWIPE VIEW - Current Item Card */}
        {viewMode === 'swipe' && currentItem && !isComplete && (
          <Fade in key={currentItem.id}>
            <Card sx={{ mb: 3, overflow: 'hidden' }}>
              <CardMedia
                component="img"
                height="250"
                image={getImageUrl(currentItem)}
                alt={currentItem.name}
                sx={{ objectFit: 'cover', bgcolor: 'grey.100' }}
                onError={(e) => { e.target.src = '/placeholder.png'; }}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {currentItem.name}
                </Typography>
                
                {currentItem.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {currentItem.description.length > 150 
                      ? `${currentItem.description.substring(0, 150)}...` 
                      : currentItem.description}
                  </Typography>
                )}

                {/* Suggestion Reason */}
                <Alert 
                  severity={currentItem.suggestion_type === 'seasonal' ? 'warning' : 'info'} 
                  icon={currentItem.item_season === 'winter' ? <WinterIcon /> : 
                        currentItem.item_season === 'summer' ? <SummerIcon /> :
                        currentItem.item_season === 'spring' ? <SpringIcon /> :
                        currentItem.item_season === 'fall' ? <FallIcon /> : <TimeIcon />}
                  sx={{ mb: 2 }}
                >
                  {currentItem.suggestion_reason || "You haven't searched for this item recently"}
                </Alert>

                {/* Tags */}
                {currentItem.custom_tags && (
                  <Box sx={{ mb: 2 }}>
                    {currentItem.custom_tags.split(',').slice(0, 5).map((tag, idx) => (
                      <Chip 
                        key={idx} 
                        label={tag.trim()} 
                        size="small" 
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                )}

                {/* AI Recommendation */}
                <Collapse in={aiRecommendation !== null}>
                  {aiRecommendation && (
                    <Alert 
                      severity={aiRecommendation.recommendation === 'donate' ? 'warning' : 'success'}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        AI suggests: {aiRecommendation.recommendation?.toUpperCase()}
                      </Typography>
                      <Typography variant="body2">
                        {aiRecommendation.reasoning}
                      </Typography>
                      {aiRecommendation.confidence && (
                        <Chip 
                          label={`Confidence: ${aiRecommendation.confidence}`} 
                          size="small" 
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Alert>
                  )}
                </Collapse>

                {/* Ask AI Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={aiLoading ? <CircularProgress size={20} /> : <AIIcon />}
                  onClick={handleAskAI}
                  disabled={aiLoading || aiRecommendation !== null}
                  sx={{ mb: 2 }}
                >
                  {aiLoading ? 'Thinking...' : aiRecommendation ? 'AI Recommendation Shown' : 'Ask AI'}
                </Button>

              </CardContent>
            </Card>
          </Fade>
        )}

        {/* Sticky Decision Buttons - Only show in swipe mode */}
        {viewMode === 'swipe' && currentItem && !isComplete && (
          <Box sx={{ 
            position: 'sticky', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            bgcolor: 'background.paper',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            zIndex: 10
          }}>
            <Box sx={{ display: 'flex', gap: 1, maxWidth: 600, mx: 'auto' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<KeepIcon />}
                onClick={() => handleDecision('keep')}
                disabled={decisionLoading}
                sx={{ flex: 1, py: 1.5 }}
              >
                Keep
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<DonateIcon />}
                onClick={() => handleDecision('donate')}
                disabled={decisionLoading}
                sx={{ flex: 1, py: 1.5 }}
              >
                Donate
              </Button>
              <Button
                variant="outlined"
                startIcon={<NotSureIcon />}
                onClick={() => handleDecision('not_sure')}
                disabled={decisionLoading}
                sx={{ flex: 1, py: 1.5 }}
              >
                Skip
              </Button>
            </Box>
          </Box>
        )}

        {/* Stats Summary */}
        {stats && !isComplete && suggestions.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            mt: 2,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">{stats.needs_review_count}</Typography>
              <Typography variant="caption" color="text.secondary">Need Review</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main">{stats.exit_bin_count}</Typography>
              <Typography variant="caption" color="text.secondary">Exit Bin</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">{stats.donated_count}</Typography>
              <Typography variant="caption" color="text.secondary">Donated</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default LetGo;
